import { useEffect, useState } from 'react';
import { PenLine, ShieldCheck, Zap, Link2, CheckCircle2, CalendarClock } from 'lucide-react';
import { useAgent } from '../hooks/useAgent';

const LEVELS = [
  ['draft_only', 'Draft only', 'Never sends. Every reply is saved for you to review.', PenLine, 'blue'],
  ['auto_safe', 'Auto-safe', 'Auto-handles only unsubscribe / out-of-office / wrong-person. Everything else drafts.', ShieldCheck, 'green'],
  ['full_auto', 'Full auto', 'Sends real emails automatically when confident.', Zap, 'amber'],
];

export default function SettingsTab() {
  const { loadSettings, saveSettings } = useAgent();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);

  useEffect(() => { loadSettings().then((s) => s && setForm(s)); }, [loadSettings]);

  if (!form) return <div className="agent-loading">Loading…</div>;

  const set = (k, v) => setForm({ ...form, [k]: v });

  const save = async () => {
    setSaving(true); setMsg(null);
    try {
      await saveSettings({
        enabled: form.enabled,
        autonomy_level: form.autonomy_level,
        booking_link: form.booking_link || null,
        meeting_title_template: form.meeting_title_template?.trim() || null,
      });
      setMsg('Saved');
    } catch (e) {
      setMsg(e?.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  return (
    <div className="agent-section">
      <div className="agent-enable-row">
        <div>
          <div className="agent-enable-title">Enable Reply Agent</div>
          <div className="agent-enable-sub">
            {form.enabled ? 'The agent is active on incoming replies.' : 'The agent is currently off.'}
          </div>
        </div>
        <button
          type="button"
          className={`agent-switch ${form.enabled ? 'on' : ''}`}
          onClick={() => set('enabled', !form.enabled)}
          aria-pressed={!!form.enabled}
        >
          <span className="agent-switch-knob" />
        </button>
      </div>

      <div className="agent-field-label">Autonomy level</div>
      <div className="agent-radio-grid">
        {LEVELS.map(([key, title, desc, Icon, tone]) => (
          <button
            type="button"
            key={key}
            className={`agent-radio-card tone-${tone} ${form.autonomy_level === key ? 'active' : ''}`}
            onClick={() => set('autonomy_level', key)}
          >
            <div className="agent-radio-icon"><Icon size={18} /></div>
            <div className="agent-radio-body">
              <div className="agent-radio-title">
                {title}
                {key === 'full_auto' && <span className="agent-warn-pill">⚠ Live sending</span>}
              </div>
              <div className="agent-radio-desc">{desc}</div>
            </div>
            <span className="agent-radio-dot" />
          </button>
        ))}
      </div>

      <div className="agent-field-label">
        <Link2 size={15} style={{ verticalAlign: '-2px', marginRight: 4 }} />
        Booking link <span className="agent-field-hint">(fallback when no calendar)</span>
      </div>
      <input className="agent-input" type="text" value={form.booking_link || ''}
        placeholder="https://cal.com/you"
        onChange={(e) => set('booking_link', e.target.value)} />

      <div className="agent-field-label">
        <CalendarClock size={15} style={{ verticalAlign: '-2px', marginRight: 4 }} />
        Meeting title format <span className="agent-field-hint">(for auto-booked meetings)</span>
      </div>
      <input className="agent-input" type="text" value={form.meeting_title_template || ''}
        placeholder="{campaign} — {rep} & {prospect}"
        onChange={(e) => set('meeting_title_template', e.target.value)} />
      <div className="agent-field-hint" style={{ marginTop: 6 }}>
        Placeholders: <code>{'{campaign}'}</code> <code>{'{rep}'}</code>{' '}
        <code>{'{prospect}'}</code> <code>{'{company}'}</code>. Leave blank for the
        default. A campaign can override this in its options.
      </div>

      <div className="agent-actions-row">
        <button className="agent-btn primary" onClick={save} disabled={saving}>
          {saving ? 'Saving…' : 'Save changes'}
        </button>
        {msg && (
          <span className="agent-msg">
            <CheckCircle2 size={15} /> {msg}
          </span>
        )}
      </div>
    </div>
  );
}
